import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Download, RefreshCw, ChevronDown, ChevronRight, Plane, Clock } from 'lucide-react';
import { CheckoutData, CheckoutEmission } from '../interfaces/Checkout';
import { checkoutService } from '../services/checkoutService';
import { DefaultLoader } from '../components';
import { EmissionFilters, FlightFilters } from '../components/EmissionFilters';
import { formatFlightTime } from '../utils/flightDateTime';
import styles from './Emissions.module.css';

const Emissions: React.FC = () => {
  const navigate = useNavigate();
  const [checkouts, setCheckouts] = useState<CheckoutData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<FlightFilters>({});
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [emissions, setEmissions] = useState<Record<string, CheckoutEmission>>({});
  const [loadingEmission, setLoadingEmission] = useState<Set<string>>(new Set());

  const loadCheckouts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await checkoutService.getAllCheckouts(currentPage, 10, filters);
      setCheckouts(response.data);
      setTotalPages(response.totalPages || Math.ceil(response.total / 10));
    } catch (err: any) {
      console.error('Erro ao carregar checkouts:', err);
      setError('Erro ao carregar dados das emissões');
    } finally {
      setLoading(false);
    }
  }, [currentPage, filters]);

  useEffect(() => {
    loadCheckouts();
  }, [loadCheckouts]);

  const handleApplyFilters = () => {
    setCurrentPage(1);
    loadCheckouts();
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
    });

  // Horário de voo: wall-clock do aeroporto (sem conversão de fuso).
  const formatTime = (timeString: string) => formatFlightTime(timeString);

  const formatDuration = (duration?: string) => duration || '';

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, string> = {
      'emission started': 'badge-pending',
      'awaiting payment': 'badge-processing',
      'completed': 'badge-completed',
      'cancelled': 'badge-cancelled',
      'failed': 'badge-failed',
    };
    const labelMap: Record<string, string> = {
      'emission started': 'Emissão Iniciada',
      'awaiting payment': 'Aguardando Pagamento',
      'completed': 'Completado',
      'cancelled': 'Cancelado',
      'failed': 'Falhou',
    };
    return (
      <span className={`badge ${statusMap[status] || 'badge-pending'}`}>
        {labelMap[status] || status}
      </span>
    );
  };

  const getPaymentStatusBadge = (paymentStatus?: string) => {
    if (!paymentStatus) return <span style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', fontStyle: 'italic' }}>—</span>;

    const config: Record<string, { label: string; color: string; bg: string; border: string }> = {
      aprovado:    { label: '✓ Aprovado',    color: 'var(--color-green)',    bg: '#f0fdf4', border: '#bbf7d0' },
      processando: { label: '⟳ Processando', color: '#b45309',               bg: '#fffbeb', border: '#fde68a' },
      reprovado:   { label: '✕ Reprovado',   color: 'var(--color-red)',      bg: '#fef2f2', border: '#fecaca' },
      reembolsado: { label: '↩ Reembolsado', color: 'var(--color-blue)',     bg: '#eff6ff', border: '#bfdbfe' },
    };

    const c = config[paymentStatus] ?? { label: paymentStatus, color: 'var(--color-darkgray)', bg: '#f1f5f9', border: '#cbd5e1' };

    return (
      <span style={{
        padding: '3px 8px', borderRadius: '4px', fontSize: 'var(--font-size-12)',
        fontWeight: 600, color: c.color, backgroundColor: c.bg, border: `1px solid ${c.border}`,
        whiteSpace: 'nowrap',
      }}>
        {c.label}
      </span>
    );
  };

  const getDepartureBadge = (departureTime: string) => {
    const now = new Date();
    const departure = new Date(departureTime);
    const diffHours = (departure.getTime() - now.getTime()) / (1000 * 60 * 60);
    const diffDays = diffHours / 24;

    let color: string, bg: string, border: string;

    if (diffHours < 0) {
      color = 'var(--color-darkgray)'; bg = '#f0f0f0'; border = '#d0d0d0';
    } else if (diffHours <= 5) {
      color = 'var(--color-red)'; bg = '#fef2f2'; border = '#fecaca';
    } else if (diffDays <= 3) {
      color = '#b45309'; bg = '#fffbeb'; border = '#fde68a';
    } else {
      color = 'var(--color-green)'; bg = '#f0fdf4'; border = '#bbf7d0';
    }

    return (
      <div style={{
        padding: '2px 6px', borderRadius: '4px', fontSize: 'var(--font-size-11)',
        fontWeight: 600, color, backgroundColor: bg, border: `1px solid ${border}`, whiteSpace: 'nowrap',
      }}>
        {departure.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
        {' '}
        {departure.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
      </div>
    );
  };

  const handleRefresh = () => { setCurrentPage(1); loadCheckouts(); };

  const toggleRowExpansion = async (checkout: CheckoutData) => {
    const checkoutId = checkout.id;
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(checkoutId)) { newSet.delete(checkoutId); } else { newSet.add(checkoutId); }
      return newSet;
    });

    if (!emissions[checkoutId] && !expandedRows.has(checkoutId)) {
      setLoadingEmission(prev => new Set(prev).add(checkoutId));
      try {
        const emission = await checkoutService.getEmission(checkoutId);
        setEmissions(prev => ({ ...prev, [checkoutId]: emission }));
      } catch (err) {
        console.error('Erro ao carregar emissão:', err);
      } finally {
        setLoadingEmission(prev => {
          const next = new Set(prev);
          next.delete(checkoutId);
          return next;
        });
      }
    }
  };

  if (loading && checkouts.length === 0) {
    return <DefaultLoader title="Emissões" messages={['Carregando emissões...', 'Processando dados de checkout...', 'Organizando informações...']} />;
  }

  return (
    <div>
      <div className={styles.pageHeader}>
        <div>
          <h2>Emissão de Passagens</h2>
          <p>Gerencie as emissões de passagens aéreas</p>
        </div>
        <div className={styles.headerActions}>
          <button onClick={handleRefresh} className="btn btn-secondary" disabled={loading}>
            <RefreshCw size={16} /> Atualizar
          </button>
          <button className="btn btn-primary">
            <Download size={16} /> Exportar
          </button>
        </div>
      </div>

      <div className="card mb-3">
        <EmissionFilters
          mode="flight"
          filters={filters}
          onChange={(f) => setFilters(f)}
          onApply={handleApplyFilters}
        />
      </div>

      {error && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: 'var(--color-red)', padding: '12px 16px', borderRadius: 'var(--br-5)', marginBottom: '20px' }}>
          {error}
        </div>
      )}

      <div className="card">
        <div className={styles.desktopTable}>
          <div className={styles.tableWrapper}>
            <table className="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Status</th>
              <th>Pagamento</th>
              <th>Passageiro</th>
              <th>Rota</th>
              <th>Valor</th>
              <th>Embarque</th>
              <th>Data</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {checkouts.length === 0 ? (
              <tr>
                <td colSpan={9} className="text-center" style={{ padding: '40px' }}>
                  {loading ? 'Carregando...' : 'Nenhuma emissão encontrada'}
                </td>
              </tr>
            ) : (
              checkouts.map((checkout) => (
                <React.Fragment key={checkout.id}>
                  <tr>
                    <td style={{ fontFamily: 'monospace', fontSize: 'var(--font-size-12)' }}>
                      {checkout.id.substring(0, 8)}...
                    </td>
                    <td>{getStatusBadge(checkout.status)}</td>
                    <td>{getPaymentStatusBadge(checkout.paymentStatus)}</td>
                    <td>
                      {checkout.passengers && checkout.passengers[0] ? (
                        <div>
                          <div style={{ fontWeight: 500 }}>
                            {checkout.passengers[0].firstName} {checkout.passengers[0].lastName}
                          </div>
                          <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>
                            {checkout.email}
                          </div>
                        </div>
                      ) : 'N/A'}
                    </td>
                    <td>
                      <div>
                        <div style={{ fontWeight: 500 }}>
                          {checkout.flightRoute || `${checkout.departureLocation} → ${checkout.arrivalLocation}`}
                        </div>
                        <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>
                          {checkout.airline} - {checkout.flightDate}
                        </div>
                        {checkout.flights && checkout.flights.length > 0 && (
                          <div style={{ fontSize: 'var(--font-size-11)', color: 'var(--color-blue)', marginTop: '2px' }}>
                            {checkout.flights.reduce((total, f) => total + f.qntStop, 0)} parada(s)
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ fontWeight: 600 }}>
                      {formatCurrency(checkout.totalPrice)}
                    </td>
                    <td>
                      {checkout.flights && checkout.flights.length > 0 ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          {checkout.flights
                            .filter(f => ['OUTBOUND', 'outbound'].includes(f.type))
                            .slice(0, 1)
                            .map(f => (
                              <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span style={{ fontSize: 'var(--font-size-10)', color: 'var(--color-darkgray)' }}>IDA</span>
                                {getDepartureBadge(f.departureTime)}
                              </div>
                            ))}
                          {checkout.flights
                            .filter(f => ['INBOUND', 'inbound'].includes(f.type))
                            .slice(0, 1)
                            .map(f => (
                              <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <span style={{ fontSize: 'var(--font-size-10)', color: 'var(--color-darkgray)' }}>VTA</span>
                                {getDepartureBadge(f.departureTime)}
                              </div>
                            ))}
                        </div>
                      ) : (
                        <span style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', fontStyle: 'italic' }}>—</span>
                      )}
                    </td>
                    <td>{formatDate(checkout.createdAt || checkout.created_at)}</td>
                    <td>
                      <div className="d-flex gap-1">
                        <button className="btn btn-secondary" style={{ padding: '6px 8px' }} onClick={() => toggleRowExpansion(checkout)}>
                          {expandedRows.has(checkout.id) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                        <button className="btn btn-secondary" style={{ padding: '6px 8px' }} onClick={() => navigate(`/emissions/${checkout.id}`)}>
                          <Eye size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>

                  {expandedRows.has(checkout.id) && (
                    <tr>
                      <td colSpan={9} style={{ padding: '0', backgroundColor: '#f8f9fa' }}>
                        <div style={{ padding: '16px' }}>
                          {/* Dados da Emissão */}
                          <div style={{ marginBottom: '20px' }}>
                            <h4 style={{ margin: '0 0 12px 0', fontSize: 'var(--font-size-16)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                              ✈️ Dados da Emissão
                            </h4>
                            {loadingEmission.has(checkout.id) ? (
                              <p style={{ color: 'var(--color-darkgray)', fontSize: 'var(--font-size-14)' }}>Carregando dados da emissão...</p>
                            ) : emissions[checkout.id] ? (
                              <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                                gap: '12px', padding: '16px',
                                backgroundColor: '#e8f5e8', borderRadius: '8px', border: '1px solid #c3e6c3',
                              }}>
                                {[
                                  { label: 'Localizador', value: emissions[checkout.id].localizador },
                                  { label: 'Parceiro', value: emissions[checkout.id].partnerName },
                                  { label: 'Forma de Pagamento', value: emissions[checkout.id].partnerPaymentMethod },
                                  { label: 'Custo por Adulto', value: `R$ ${parseFloat(emissions[checkout.id].costPerAdult).toFixed(2)}` },
                                  ...(emissions[checkout.id].costPerChild ? [{ label: 'Custo por Criança', value: `R$ ${parseFloat(emissions[checkout.id].costPerChild!).toFixed(2)}` }] : []),
                                  ...(emissions[checkout.id].costPerInfant ? [{ label: 'Custo por Infante', value: `R$ ${parseFloat(emissions[checkout.id].costPerInfant!).toFixed(2)}` }] : []),
                                  { label: 'Taxa de Embarque', value: `R$ ${parseFloat(emissions[checkout.id].boardingFee).toFixed(2)}` },
                                  { label: 'Custo Total', value: `R$ ${parseFloat(emissions[checkout.id].totalCost).toFixed(2)}` },
                                  { label: 'Emitido por', value: emissions[checkout.id].emittedBy },
                                  { label: 'Emitido em', value: formatDate(emissions[checkout.id].emittedAt) },
                                ].map(({ label, value }) => (
                                  <div key={label}>
                                    <div style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', marginBottom: '2px' }}>{label}</div>
                                    <div style={{ fontWeight: 600, fontSize: 'var(--font-size-14)' }}>{value}</div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <p style={{ color: 'var(--color-darkgray)', fontSize: 'var(--font-size-14)' }}>Não foi possível carregar os dados da emissão.</p>
                            )}
                          </div>

                          <h4 style={{ margin: '0 0 12px 0', fontSize: 'var(--font-size-16)' }}>
                            Detalhes dos Voos
                          </h4>

                          {checkout.flights && checkout.flights.length > 0 ? (
                            checkout.flights.map((flight) => {
                              const sortedStops = flight.stops ? [...flight.stops].sort((a, b) => a.stopOrder - b.stopOrder) : [];
                              const isOutbound = ['OUTBOUND', 'outbound'].includes(flight.type);

                              // Monta os trechos: origem → parada1 → parada2 → destino
                              type Segment = {
                                key: string;
                                flightNum: string;
                                company: string;
                                from: string;
                                fromTime: string;
                                to: string;
                                toTime: string;
                                duration?: string;
                                waitAfter?: string;
                                isEscale?: boolean;
                              };

                              const segments: Segment[] = sortedStops.length > 0
                                ? sortedStops.map((stop, i) => ({
                                    key: stop.id,
                                    flightNum: stop.flightNumber,
                                    company: stop.operantCompany || stop.companyCode || flight.airlineName,
                                    from: stop.departureLocation,
                                    fromTime: stop.departureTime,
                                    to: stop.arrivalLocation,
                                    toTime: stop.arrivalTime,
                                    duration: stop.flightDuration,
                                    waitAfter: sortedStops[i + 1] ? stop.timeWaiting : undefined,
                                    isEscale: stop.isEscale,
                                  }))
                                : [{
                                    key: flight.id,
                                    flightNum: flight.flightNumber,
                                    company: flight.airlineName,
                                    from: flight.departureLocation,
                                    fromTime: flight.departureTime,
                                    to: flight.arrivalLocation,
                                    toTime: flight.arrivalTime,
                                    duration: flight.flightDuration,
                                  }];

                              return (
                                <div key={flight.id} style={{ marginBottom: '20px', border: '1px solid #e0e0e0', borderRadius: '10px', overflow: 'hidden', backgroundColor: 'white' }}>
                                  {/* Header do voo */}
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', backgroundColor: isOutbound ? '#eff6ff' : '#f0fdf4', borderBottom: '1px solid #e0e0e0' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <Plane size={15} color={isOutbound ? 'var(--color-blue)' : 'var(--color-green)'} />
                                      <strong style={{ fontSize: 'var(--font-size-14)', color: isOutbound ? 'var(--color-blue)' : 'var(--color-green)' }}>
                                        {isOutbound ? 'IDA' : 'VOLTA'}
                                      </strong>
                                      <span style={{ fontSize: 'var(--font-size-13)', color: 'var(--color-darkgray)' }}>
                                        {flight.airlineName} · {flight.departureLocation} → {flight.arrivalLocation}
                                      </span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                      <Clock size={13} color="var(--color-darkgray)" />
                                      <span style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>{formatDuration(flight.flightDuration)}</span>
                                      {flight.qntStop > 0 && (
                                        <span style={{ fontSize: 'var(--font-size-11)', color: 'var(--color-orange)', background: '#fff7ed', border: '1px solid #fed7aa', padding: '1px 8px', borderRadius: '10px' }}>
                                          {flight.qntStop} parada{flight.qntStop > 1 ? 's' : ''}
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Timeline de trechos */}
                                  <div style={{ padding: '16px 20px' }}>
                                    {segments.map((seg, idx) => (
                                      <div key={seg.key}>
                                        {/* Trecho */}
                                        <div style={{ display: 'flex', alignItems: 'stretch', gap: '0' }}>
                                          {/* Coluna esquerda: aeroportos + linha */}
                                          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '32px', flexShrink: 0 }}>
                                            {/* Ponto de origem */}
                                            <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: idx === 0 ? 'var(--color-blue)' : 'var(--color-orange)', border: '2px solid white', boxShadow: '0 0 0 2px ' + (idx === 0 ? 'var(--color-blue)' : 'var(--color-orange)'), flexShrink: 0, marginTop: '4px' }} />
                                            {/* Linha vertical */}
                                            <div style={{ flex: 1, width: '2px', background: 'linear-gradient(to bottom, #cbd5e1, #cbd5e1)', minHeight: '40px' }} />
                                            {/* Ponto de destino (só no último trecho) */}
                                            {idx === segments.length - 1 && (
                                              <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: isOutbound ? 'var(--color-blue)' : 'var(--color-green)', border: '2px solid white', boxShadow: '0 0 0 2px ' + (isOutbound ? 'var(--color-blue)' : 'var(--color-green)'), flexShrink: 0, marginBottom: '4px' }} />
                                            )}
                                          </div>

                                          {/* Conteúdo do trecho */}
                                          <div style={{ flex: 1, paddingLeft: '12px', paddingBottom: idx < segments.length - 1 ? '0' : '4px' }}>
                                            {/* Origem do trecho */}
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '4px' }}>
                                              <div>
                                                <span style={{ fontWeight: 700, fontSize: 'var(--font-size-16)', letterSpacing: '0.5px' }}>{seg.from}</span>
                                                <span style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', marginLeft: '8px' }}>
                                                  {formatTime(seg.fromTime)}
                                                </span>
                                              </div>
                                              <div style={{ textAlign: 'right', fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)' }}>
                                                <span style={{ fontWeight: 500 }}>{seg.company}</span>
                                                <span style={{ marginLeft: '6px', fontFamily: 'monospace', background: '#f1f5f9', padding: '1px 6px', borderRadius: '4px' }}>{seg.flightNum}</span>
                                              </div>
                                            </div>

                                            {/* Duração do trecho */}
                                            {seg.duration && (
                                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', margin: '6px 0', paddingLeft: '4px' }}>
                                                <div style={{ width: '20px', height: '1px', background: '#cbd5e1' }} />
                                                <span style={{ fontSize: 'var(--font-size-11)', color: '#94a3b8' }}>{formatDuration(seg.duration)}</span>
                                                <Plane size={10} color="#94a3b8" style={{ transform: 'rotate(0deg)' }} />
                                              </div>
                                            )}

                                            {/* Destino do trecho (só no último) */}
                                            {idx === segments.length - 1 && (
                                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <div>
                                                  <span style={{ fontWeight: 700, fontSize: 'var(--font-size-16)', letterSpacing: '0.5px' }}>{seg.to}</span>
                                                  <span style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', marginLeft: '8px' }}>
                                                    {formatTime(seg.toTime)}
                                                  </span>
                                                </div>
                                              </div>
                                            )}
                                          </div>
                                        </div>

                                        {/* Conexão entre trechos (escala/parada) */}
                                        {idx < segments.length - 1 && (
                                          <div style={{ display: 'flex', alignItems: 'stretch', gap: '0' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '32px', flexShrink: 0 }}>
                                              <div style={{ flex: 1, width: '2px', background: '#cbd5e1', minHeight: '8px' }} />
                                            </div>
                                            <div style={{ flex: 1, paddingLeft: '12px', paddingBottom: '0' }}>
                                              {/* Chegada no aeroporto de conexão */}
                                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                                <div>
                                                  <span style={{ fontWeight: 700, fontSize: 'var(--font-size-16)', letterSpacing: '0.5px' }}>{seg.to}</span>
                                                  <span style={{ fontSize: 'var(--font-size-12)', color: 'var(--color-darkgray)', marginLeft: '8px' }}>
                                                    {formatTime(seg.toTime)}
                                                  </span>
                                                </div>
                                              </div>
                                              {/* Badge de espera */}
                                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '5px 12px', backgroundColor: seg.isEscale ? '#fef2f2' : '#fff7ed', border: `1px solid ${seg.isEscale ? '#fecaca' : '#fed7aa'}`, borderRadius: '20px', marginBottom: '6px' }}>
                                                <Clock size={11} color={seg.isEscale ? 'var(--color-red)' : 'var(--color-orange)'} />
                                                <span style={{ fontSize: 'var(--font-size-11)', fontWeight: 600, color: seg.isEscale ? 'var(--color-red)' : 'var(--color-orange)' }}>
                                                  {seg.isEscale ? 'Escala' : 'Conexão'}
                                                  {seg.waitAfter ? ` · ${seg.waitAfter}` : ''}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              );
                            })
                          ) : (
                            <p style={{ color: 'var(--color-darkgray)' }}>Nenhum detalhe de voo disponível</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>
        </table>
          </div>
        </div>

        {/* Mobile Cards */}
        <div className={styles.mobileCards}>
          {checkouts.length === 0 ? (
            <p className="text-center" style={{ padding: '40px' }}>
              {loading ? 'Carregando...' : 'Nenhuma emissão encontrada'}
            </p>
          ) : (
            checkouts.map((checkout) => (
              <div key={checkout.id} className={styles.mobileCard}>
                <div className={styles.mobileCardRow}>
                  <span className={styles.mobileCardLabel}>ID</span>
                  <span className={styles.mobileCardValue} style={{ fontFamily: 'monospace' }}>
                    {checkout.id.substring(0, 8)}...
                  </span>
                </div>
                <div className={styles.mobileCardRow}>
                  <span className={styles.mobileCardLabel}>Status</span>
                  <span className={styles.mobileCardValue}>{getStatusBadge(checkout.status)}</span>
                </div>
                <div className={styles.mobileCardRow}>
                  <span className={styles.mobileCardLabel}>Pagamento</span>
                  <span className={styles.mobileCardValue}>{getPaymentStatusBadge(checkout.paymentStatus)}</span>
                </div>
                <div className={styles.mobileCardRow}>
                  <span className={styles.mobileCardLabel}>Passageiro</span>
                  <span className={styles.mobileCardValue}>
                    {checkout.passengers?.[0] ? `${checkout.passengers[0].firstName} ${checkout.passengers[0].lastName}` : 'N/A'}
                  </span>
                </div>
                <div className={styles.mobileCardRow}>
                  <span className={styles.mobileCardLabel}>Rota</span>
                  <span className={styles.mobileCardValue}>
                    {checkout.flightRoute || `${checkout.departureLocation} → ${checkout.arrivalLocation}`}
                  </span>
                </div>
                <div className={styles.mobileCardRow}>
                  <span className={styles.mobileCardLabel}>Valor</span>
                  <span className={styles.mobileCardValue} style={{ fontWeight: 600 }}>
                    {formatCurrency(checkout.totalPrice)}
                  </span>
                </div>
                <div className={styles.mobileCardRow}>
                  <span className={styles.mobileCardLabel}>Data</span>
                  <span className={styles.mobileCardValue}>{formatDate(checkout.createdAt || checkout.created_at)}</span>
                </div>
                <div className={styles.mobileCardActions}>
                  <button className="btn btn-secondary" style={{ padding: '6px 8px' }} onClick={() => toggleRowExpansion(checkout)}>
                    {expandedRows.has(checkout.id) ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                  </button>
                  <button className="btn btn-secondary" style={{ padding: '6px 8px' }} onClick={() => navigate(`/emissions/${checkout.id}`)}>
                    <Eye size={14} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {totalPages > 1 && (
          <div className={styles.pagination}>
            <button onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1} className="btn btn-secondary" style={{ padding: '8px 12px' }}>
              Anterior
            </button>
            <span style={{ margin: '0 16px' }}>Página {currentPage} de {totalPages}</span>
            <button onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages} className="btn btn-secondary" style={{ padding: '8px 12px' }}>
              Próxima
            </button>
          </div>
        )}
      </div>

      <div className={styles.statsGrid}>
        <div className="card text-center">
          <h4 style={{ margin: '0 0 8px 0', color: 'var(--color-orange)' }}>
            {checkouts.filter(c => c.status === 'emission started').length}
          </h4>
          <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>Emissão Iniciada</p>
        </div>
        <div className="card text-center">
          <h4 style={{ margin: '0 0 8px 0', color: 'var(--color-blue)' }}>
            {checkouts.filter(c => c.status === 'awaiting payment').length}
          </h4>
          <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>Aguardando Pagamento</p>
        </div>
        <div className="card text-center">
          <h4 style={{ margin: '0 0 8px 0', color: 'var(--color-green)' }}>
            {checkouts.filter(c => c.status === 'completed').length}
          </h4>
          <p style={{ margin: 0, fontSize: 'var(--font-size-14)' }}>Completados</p>
        </div>
      </div>
    </div>
  );
};

export default Emissions;
